import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Flex,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from "../../../../shared/providers/AuthProvider/AuthProvider";
import FormErrorResetEffect from "../LoginPage/FormErrorResetEffect";
import InlineErrorInput from "../../../../shared/components/InlineErrorInput/InlineErrorInput";
import { useAppToast } from "../../../../hooks/useAppToast";

const ResetPasswordRequestPage = () => {
  const { sendPasswordResetEmail } = useAuth();
  const { t } = useTranslation('default');
  const toast = useAppToast();
  const [error, setError] = useState(null);

  useEffect(() => () => setError(null), []);

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t('form.email.message'))
      .required(t('form.email.required')),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setError(null);
    try {
      await sendPasswordResetEmail(values.email);
      resetForm();
      toast({
        title: t('reset.request.successTitle'),
        description: t('reset.request.successMessage'),
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } catch (e) {
      if (e?.response?.data?.code === 'UserNotConfirmed') {
        setError(t('reset.request.notActivated'));
      } else {
        setError(e?.response?.data?.message || 'reset.request.error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex
      bgColor="#F3F3F3"
      w="100%"
      minH="100vh"
      align="center"
      justify="center"
      px={{ base: 4, sm: 6 }}
    >
      <Box w="full" align="center" display="flex" gap={'48px'} flexDirection="column" alignItems="center">
        <Stack spacing={6} align="center" mb={8}>
          <Text className="login-title">{t('reset.request.title')}</Text>
          <Text className="login-message" lineHeight={{ base: '16px', sm: 'sm' }} fontSize={{ base: 20, sm: 24 }}>
            {t('reset.request.message')}
          </Text>
        </Stack>

        <Box maxW="3xl" w="full" bg="transparent" gap={'48px'}>
          <Formik
            initialValues={{ email: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnChange
            validateOnBlur
          >
            {({ isSubmitting, errors, touched, isValid, dirty, values }) => (
              <Form>
                <FormErrorResetEffect resetError={() => setError(null)} />
                <Flex flexDirection="column" gap={'16px'}>
                  <InlineErrorInput
                    label={t('form.email.label')}
                    name="email"
                    type="email"
                    placeholder={t('form.email.placeholder')}
                    value={values.email}
                    errors={errors.email}
                    touched={touched.email}
                    backgroundColor="white"
                  />
                </Flex>

                {error && (
                  <Text color="red.500" textAlign="center" mt={4}>
                    {t(error)}
                  </Text>
                )}

                <Box display="flex" flexDirection="column" alignItems="center" mt={8}>
                  <Button
                    className="login-button"
                    type="submit"
                    isLoading={isSubmitting}
                    gap={'10px'}
                    bg="#089A7A"
                    _hover={{ bg: '#1A7743' }}
                    h={'59px'}
                    w={'250px'}
                    borderRadius={'4px'}
                    isDisabled={isSubmitting || !isValid || !dirty}
                  >
                    <Text className="login-button-label">
                      {t('reset.request.button')}
                    </Text>
                  </Button>
                </Box>

                <Box textAlign="center" mt={6} className="login-register-message">
                  {t('register.login.message')}
                  <Link href="/login" color="#089A7A" _hover={{ textDecoration: 'underline' }} ml={1}>
                    {t('login.button')}
                  </Link>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </Flex>
  );
};

export default ResetPasswordRequestPage;
